import React from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  getColumnWidth,
  ContainerDisplays,
  ContainerFlexGaps,
  ContainerPaddings,
  ContainerFlexDirections,
  Container as UIFoundationsContainer,
  Slider,
  useThemeUtils,
  useResponsiveRender
} from '@dcxwam/dcx-wam-ui-foundations'
import { slidesArray } from '../Slides/Slides'
import { canUseDom } from '../../../../utils/isSsr'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import { SlidesArrayProps } from '../types'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import { NoSsr } from '../../../functional/NoSsr/NoSsr'
import { getSliderComponent } from '../common/getSliderComponent'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'

if (canUseDom) {
  import('@dcxwam/dcx-wam-ui-foundations/css')
}

interface SliderGridLayoutProps {
  gridItems: Array<React.ReactNode>
  index: number
}

interface SliderDesktopGridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[]
  background: string
}

export interface SliderVariantEProps extends Partial<SlidesArrayProps> {
  accessibilityFirst: string
  accessibilityLast: string
  accessibilityNext: string
  accessibilityPrevious: string
  id: string
  background: string
  adaptiveHeight: boolean
  xfPath?: string
}

const SliderGridLayout = ({ gridItems, index }: SliderGridLayoutProps) => {
  const firstColumn = gridItems.slice(0, 2)
  const secondColumn = gridItems.slice(2, gridItems.length)
  const themeUtils = useThemeUtils()

  const configContainerWrapper = {
    display: ContainerDisplays.Flex,
    flexGap: ContainerFlexGaps.Size_3,
    // fallback padding in case there is multiple grids
    ...(index > 0 && {
      padding: themeUtils.getResponsiveProp([{ t: ContainerPaddings.Size_3 }])
    })
  }

  const configContainerColumn = {
    width: '50%',
    display: ContainerDisplays.Flex,
    flexDirection: ContainerFlexDirections.Column,
    flexGap: ContainerFlexGaps.Size_3
  }

  return (
    <UIFoundationsContainer {...configContainerWrapper}>
      <UIFoundationsContainer {...configContainerColumn}>
        {firstColumn.map((element: React.ReactNode) => element)}
      </UIFoundationsContainer>
      <UIFoundationsContainer {...configContainerColumn}>
        {secondColumn.map((element: React.ReactNode) => element)}
      </UIFoundationsContainer>
    </UIFoundationsContainer>
  )
}

const SliderDesktopGrid = ({
  items = [],
  background
}: SliderDesktopGridProps) => {
  const chunks = []
  for (let i = 0; i < items.length; i += 4) {
    chunks.push(items.slice(i, i + 4))
  }

  return (
    <UIFoundationsContainer background={background}>
      {chunks.map((grid: Array<React.ReactNode>, i: number) => (
        <SliderGridLayout
          gridItems={grid}
          index={i}
          key={`grid-${grid.length}-${i}`}
        />
      ))}
    </UIFoundationsContainer>
  )
}

const SliderVariantE: React.FC<
  EditableComponentPropsType<SliderVariantEProps>
> = withComponentRefresh((props) => {
  const {
    accessibilityFirst,
    accessibilityLast,
    accessibilityNext,
    accessibilityPrevious,
    id,
    background,
    adaptiveHeight,
    xfPath,
    ...rest
  } = props.model
  const editableProps = useExtractEditableProps(props, rest)

  const themeUtils = useThemeUtils()

  const slides = slidesArray({
    ...(props as unknown as SlidesArrayProps)
  })
  const slidesDesktopGrid = slidesArray({
    ...(props as unknown as SlidesArrayProps),
    height: 'auto'
  })
  const configSliderE = {
    className: 'slider-e',
    background,
    centeredSlides: true,
    slides,
    lazyLoading: true,
    keyboard: true,
    autoHeight: adaptiveHeight,
    verticalPadding: false,
    numberOfColumns: themeUtils.getResponsiveProp([getColumnWidth(12, 12)]),
    loop: false,
    sliderPadding: themeUtils.getResponsiveProp([
      { x: ContainerPaddings.Size_4 }
    ]),
    propsPerBreakpoints: themeUtils.getResponsiveProp([
      {
        slidesPerView: 1,
        spaceBetween: Number(themeUtils.getComputedSpacing(Number(3), true))
      }
    ])
  }

  const render = useResponsiveRender([
    <Slider key="mobile" {...configSliderE} />,
    null,
    <SliderDesktopGrid
      key="desktop"
      items={slidesDesktopGrid}
      background={background}
    />
  ])

  const componentToDisplay = getSliderComponent({
    props,
    xfPath,
    renderComponent: render
  })

  return (
    <NoSsr>
      <EditableComponent {...editableProps}>
        {componentToDisplay}
      </EditableComponent>
    </NoSsr>
  )
})

const SliderVariantEWithErrorBoundary = withErrorBoundary(SliderVariantE, {
  componentName: 'SliderVariantE',
  fallback: <ErrorFallback componentName="SliderVariantE" />
})

export default SliderVariantEWithErrorBoundary
